import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import type { MatchResultDetailNode } from "./use-match-editor";

/**
 * WHICH SAVED RESULT IS OPEN, and it belongs in the URL.
 *
 * Both result steps let the reader open a saved result and then follow a link out of the match —
 * to a person on one side, to a company on the other. Browser back has to land back IN that result,
 * and a `useState` alone cannot do it: the step remounts with an empty state and the reader is
 * dropped on the result list of step 3, one navigation short of where they were.
 *
 * The person step has carried this since its own review; the company step was built beside it with
 * the plain `useState` and lost the restore. Extracted here so there is one answer to "which result
 * is open" rather than two — the same move that `RankingRow` and `RankingTopFive` make for the list
 * below it.
 *
 * THREE THINGS THIS GETS RIGHT that a second hand-rolled copy would not:
 *
 * 1. REPLACE, not push. Opening and closing a result inside the list would otherwise stack history
 *    entries, and the reader's back button would walk through their own clicks instead of leaving
 *    the match.
 * 2. The cold restore compares against the id this hook LAST OPENED, tracked in a ref that only
 *    `open`, `close` and the effect itself write. Compared against the state instead, the effect
 *    would see "url says X, state says null" for the one render between clearing the state and the
 *    param navigation landing, and would re-fetch the result the reader just closed.
 * 3. A stale param is stripped. A result that was deleted meanwhile, or that the reader may not
 *    see, would otherwise leave `?result=<id>` in the URL and re-fail on every mount.
 */
export function useOpenedMatchResult(
  openResult: (id: string) => Promise<MatchResultDetailNode | null>,
) {
  const intl = useIntl();
  const [openedResult, setOpenedResult] = useState<MatchResultDetailNode | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlResultId = searchParams.get("result");

  const writeResultParam = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (id) params.set("result", id);
          else params.delete("result");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const loadFailed = useCallback(
    (id: string) =>
      intl.formatMessage(
        {
          id: "match.result.loadFailed",
          defaultMessage:
            "Ergebnis konnte nicht geladen werden (id {id}). Browser-Konsole prüfen.",
        },
        { id },
      ),
    [intl],
  );

  const fetchInFlight = useRef<string | null>(null);
  // The id this hook currently holds open. Written in `open`, `close` and the effect below —
  // never during render, which is both what the lint rule asks for and the honest description: it
  // is a record of what happened, not a value the markup reads.
  const openedIdRef = useRef<string | null>(null);
  // Has the reader decided, in THIS mount, what is open? Once they have, the state is the only
  // authority and the param is merely its mirror.
  //
  // Without this the close is undone: `close` clears the state and asks for the navigation, the
  // navigation lands a render later, and in between the effect sees a url naming a result that
  // nothing holds open — so it dutifully fetches back what the reader just closed. Restoring is a
  // MOUNT concern (the reader came back to a url), and this says so instead of trying to tell the
  // two situations apart from the values.
  const readerDecided = useRef(false);

  useEffect(() => {
    if (readerDecided.current) return;
    if (!urlResultId) return;
    if (openedIdRef.current === urlResultId) return;
    if (fetchInFlight.current === urlResultId) return;
    fetchInFlight.current = urlResultId;
    void (async () => {
      const detail = await openResult(urlResultId);
      if (fetchInFlight.current !== urlResultId) return;
      fetchInFlight.current = null;
      if (detail) {
        openedIdRef.current = urlResultId;
        setOpenedResult(detail);
      } else {
        openedIdRef.current = null;
        writeResultParam(null);
        setOpenError(loadFailed(urlResultId));
      }
    })();
  }, [urlResultId, openResult, writeResultParam, loadFailed]);

  /** Loads the result and opens it. Returns whether it could be opened. */
  const open = useCallback(
    async (id: string) => {
      readerDecided.current = true;
      setOpenError(null);
      const detail = await openResult(id);
      if (!detail) {
        setOpenError(loadFailed(id));
        return false;
      }
      openedIdRef.current = id;
      setOpenedResult(detail);
      writeResultParam(id);
      return true;
    },
    [openResult, writeResultParam, loadFailed],
  );

  /** Back to the live draft. Clears state and the param together. */
  const close = useCallback(() => {
    readerDecided.current = true;
    openedIdRef.current = null;
    setOpenedResult(null);
    writeResultParam(null);
  }, [writeResultParam]);

  return { openedResult, openError, open, close };
}
